import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGridPro, type GridColDef } from "@mui/x-data-grid-pro";
import { type FC, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import { PageHeader } from "../../modules/layout/components/page-header";
import { PageHeading } from "../../modules/layout/components/page-heading";
import { useSupabaseCallback, useSupabaseLoader } from "../../server/supabase/hooks";
import { CeAdminPageHeader } from "./ce-admin-page-header";
import { slackAdmin } from "../../modules/slack-admin/slack-admin";
import { uuidv4 } from "../../utils/uuid";
import type { UUID } from "../../utils/uuid";
import yup from "../../utils/yup";
import { authHeader } from "../../utils/auth-header";
import { useToken } from "../../server/auth/use-token";

type Account = {
  id: UUID;
  name: string;
};

type SlackAppConfigRow = {
  id: UUID;
  account_id: UUID;
  account_name: string;
  name: string | null;
  app_id: string | null;
  version: string;
  slack_config_id: UUID | null;
  verified: boolean | null;
  created_at: string;
};

const schema = yup.object().shape({
  name: yup.string().required("App name is required").default(""),
  account: yup
    .object()
    .shape({
      id: yup.string().required(),
      name: yup.string().required(),
    })
    .required("Account is required")
    .default(undefined),
});

type SlackAppForm = {
  name: string;
  account: Account;
};

const SlackAppsPage: FC = () => {
  const [creating, setCreating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingData, setPendingData] = useState<SlackAppForm | null>(null);
  const getToken = useToken();

  const {
    data: accounts,
    loading: accountsLoading,
    error: accountsError,
  } = useSupabaseLoader<Account[], "ce_admin">(
    async ({ supabase }) => {
      const { data, error } = await supabase.from("account_limits").select("id,name").order("name");

      if (error) throw new Error("Could not load accounts");
      return (data ?? []).map((a) => ({ id: a.id, name: a.name }));
    },
    [],
    "ce_admin",
  );

  const {
    data: slackApps,
    loading: slackAppsLoading,
    error: slackAppsError,
    reload: reloadSlackApps,
  } = useSupabaseLoader<SlackAppConfigRow[], "ce_admin">(
    async ({ supabase }) => {
      const { data, error } = await supabase
        .from("slack_apps")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw new Error("Could not load slack app configs");
      return (data ?? []) as SlackAppConfigRow[];
    },
    [],
    "ce_admin",
  );

  const { control, handleSubmit, reset } = useForm<SlackAppForm>({
    defaultValues: { name: "", account: undefined },
    resolver: yupResolver(schema) as never,
  });

  const handleFormSubmit = (data: SlackAppForm): void => {
    setPendingData(data);
    setConfirmOpen(true);
  };

  const handleConfirmClose = (): void => {
    setConfirmOpen(false);
    setPendingData(null);
  };

  const onConfirmedCreate = useSupabaseCallback<[], void>(
    async ({ supabase }) => {
      if (!pendingData) return;

      setConfirmOpen(false);
      setCreating(true);
      try {
        const slackAppId = uuidv4();

        const { data: account, error: accountError } = await supabase
          .from("account")
          .select("domain")
          .eq("id", pendingData.account.id)
          .single();

        if (accountError || !account) {
          toast.error("Failed to fetch account details");
          return;
        }

        if (!account.domain) {
          toast.error("Account does not have a domain set");
          return;
        }

        const response = await slackAdmin.POST("/create-app", {
          body: {
            slack_app_id: slackAppId,
            account_id: pendingData.account.id,
            display_name: pendingData.name,
            org_name: account.domain,
          },
          ...authHeader(await getToken()),
        });

        if (response.error) {
          const errBody = response.error as { error?: string };
          toast.error(errBody.error ?? "Failed to create Slack app");
          return;
        }

        toast.success(
          `Slack app "${pendingData.name}" created successfully. Client ID: ${response.data.client_id}`,
        );
        reset();
        reloadSlackApps();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "An unexpected error occurred");
      } finally {
        setCreating(false);
        setPendingData(null);
      }
    },
    [getToken, reset, reloadSlackApps, pendingData],
  );

  const columns: GridColDef<SlackAppConfigRow>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "App Name",
        flex: 1,
        valueGetter: (_value, row) => row.name ?? "—",
      },
      {
        field: "account_name",
        headerName: "Account",
        flex: 1,
        valueGetter: (_value, row) => row.account_name,
      },
      {
        field: "version",
        headerName: "Version",
        width: 100,
      },
      {
        field: "app_id",
        headerName: "Slack App ID",
        flex: 1,
        valueGetter: (_value, row) => row.app_id ?? "Not created",
      },
      {
        field: "verified",
        headerName: "Verified",
        width: 100,
        renderCell: ({ row }) =>
          row.verified ? (
            <Chip label="Yes" color="success" size="small" />
          ) : (
            <Chip label="No" color="default" size="small" />
          ),
      },
      {
        field: "slack_config_id",
        headerName: "Has Config",
        width: 120,
        renderCell: ({ row }) =>
          row.slack_config_id ? (
            <Chip label="Connected" color="success" size="small" />
          ) : (
            <Chip label="No Config" color="warning" size="small" />
          ),
      },
    ],
    [],
  );

  const loading = accountsLoading || slackAppsLoading;

  return (
    <Page title="Administration | ChangeEngine">
      <PageHeader display="flex">
        <PageHeading heading={<CeAdminPageHeader />} subheading="Manage Slack Apps" />
      </PageHeader>
      <PageContent>
        <Stack spacing={3}>
          <Paper elevation={24} sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Create Slack App
            </Typography>

            {accountsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load accounts
              </Alert>
            )}

            <form
              onSubmit={(e) => {
                void handleSubmit(handleFormSubmit)(e);
              }}
            >
              <Stack spacing={2}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="App Name"
                      fullWidth
                      variant="standard"
                      helperText={error?.message ?? "The name for the new Slack app"}
                      error={Boolean(error)}
                    />
                  )}
                />

                <Controller
                  name="account"
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <FormControl variant="standard" fullWidth error={Boolean(error)}>
                      <InputLabel>Account</InputLabel>
                      <Select
                        value={value?.id ?? ""}
                        onChange={(e) => {
                          const selected = (accounts ?? []).find((a) => a.id === e.target.value);
                          onChange(selected ?? null);
                        }}
                      >
                        {(accounts ?? []).map((account) => (
                          <MenuItem key={account.id} value={account.id}>
                            {account.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {error?.message ?? "Select the account to create the Slack app for"}
                      </FormHelperText>
                    </FormControl>
                  )}
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    data-analytics-id="ce-admin-create-slack-app"
                    type="submit"
                    variant="contained"
                    disabled={creating || accountsLoading}
                  >
                    {creating ? "Creating…" : "Create Slack App"}
                  </Button>
                </Box>
              </Stack>
            </form>
          </Paper>

          <Paper elevation={24} sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Existing Slack Apps
            </Typography>

            {loading && <LinearProgress />}
            {slackAppsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load Slack apps
              </Alert>
            )}

            {slackApps && (
              <DataGridPro<SlackAppConfigRow>
                rows={slackApps}
                columns={columns}
                disableRowSelectionOnClick
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[10, 25, 50]}
                pagination
              />
            )}
          </Paper>
        </Stack>
      </PageContent>
      <Dialog open={confirmOpen} onClose={handleConfirmClose}>
        <DialogTitle>Confirm Slack App Creation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will <strong>build and distribute</strong> a new Slack app. This action is
            rate-limited to <strong>1 per day</strong>. Please verify the details below before
            confirming.
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Account:</strong> {pendingData?.account?.name}
            </Typography>
            <Typography variant="body2">
              <strong>App Name:</strong> {pendingData?.name}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            data-analytics-id="ce-admin-create-slack-app-cancel"
            onClick={handleConfirmClose}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            data-analytics-id="ce-admin-create-slack-app-confirm"
            onClick={() => {
              void onConfirmedCreate();
            }}
            variant="contained"
            color="warning"
            disabled={creating}
          >
            {creating ? "Creating…" : "Confirm & Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export default SlackAppsPage;
